import {gql} from '@apollo/client';
import {useCallback} from 'react';

import {ImplementedAntibodySchemaInterfacesQuery} from 'antibodies/hooks/__generated/ImplementedAntibodySchemaInterfacesQuery';
import {isBiologicsEnabled} from 'antibodies/util/biologicsCapability';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';

export type ImplementedAntibodySchemaInterface =
  ImplementedAntibodySchemaInterfacesQuery['allImplementedAntibodySchemaInterfaces'][number];

export const IMPLEMENTED_ANTIBODY_SCHEMA_INTERFACES_QUERY = gql`
  query ImplementedAntibodySchemaInterfacesQuery @skipAudit @engComponent(name: ANTIBODIES) {
    allImplementedAntibodySchemaInterfaces {
      id
      format {
        id
      }
      schema {
        id
        name
        archived
      }
    }
  }
`;

/**
 * Fetches all implemented antibody schema interfaces. Skips if antibodies are not enabled.
 *
 * The selection set is deliberately minimal. This runs on every page that renders a create menu, and the
 * server cost scales with the number of antibody schemas on the tenant, so only fields a caller reads
 * belong here.
 */
export function useImplementedAntibodySchemaInterfacesQuery(): {
  /* The current status of the query */
  status: QueryResultType;
  /* The schema interfaces. Returns empty list if skipped, or if the query is not successful. */
  implementedAntibodySchemaInterfaces: Array<ImplementedAntibodySchemaInterface>;
  /* Refetches the query from the network, returning the updated list. */
  refetch: () => Promise<Array<ImplementedAntibodySchemaInterface>>;
} {
  const result = useSkippableBenchlingQuery<ImplementedAntibodySchemaInterfacesQuery>(
    IMPLEMENTED_ANTIBODY_SCHEMA_INTERFACES_QUERY,
    {
      skip: !isBiologicsEnabled(),
    }
  );

  const {refetch: queryRefetch} = result;

  const refetch = useCallback(async (): Promise<Array<ImplementedAntibodySchemaInterface>> => {
    const refetchResult = await queryRefetch();
    return refetchResult.data?.allImplementedAntibodySchemaInterfaces ?? [];
  }, [queryRefetch]);

  if (result.type !== QueryResultType.SUCCESS) {
    return {status: result.type, implementedAntibodySchemaInterfaces: [], refetch};
  }

  return {
    status: QueryResultType.SUCCESS,
    implementedAntibodySchemaInterfaces: result.data.allImplementedAntibodySchemaInterfaces,
    refetch,
  };
}
